/**
 * IT > Service Desk > one knowledge-base article (/it/service-desk/kb/:id, or
 * /kb/new). Title, summary, type, who sees it, related categories, topics, and the body in the
 * shared rich-text editor. Images in the body upload to the article, so they
 * work once the article has been saved as a draft at least once.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { RichTextEditor, formatInstant, type SdAudience } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  articleImageUploader,
  useArticleStaff,
  useCategoriesAll,
  useCreateArticle,
  usePatchArticle,
  usePublishArticle,
  useRetireArticle,
  useTopics,
} from '@/lib/useServiceDesk';
import { uploadEditorImage } from '@/lib/editorImages';
import { sdHtml, sdHtmlForStorage } from '@/lib/serviceDeskClient';
import type { SdArticleCategory, SdArticleInput, SdArticleType } from '@/lib/serviceDeskTypes';
import { SdError, SdShell } from './SdShell';
import { CategoryPicker } from './CategoryPicker';
import { activeOnly, pathLabel } from './categoryTree';
import {
  ARTICLE_STATUS_LABELS,
  ARTICLE_STATUS_TONES,
  ARTICLE_TYPE_LABELS,
  AUDIENCE_LABELS,
  EDITABLE_AUDIENCES,
} from './sdLabels';
import { SD_BTN_DANGER, SD_BTN_GHOST, SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

export function KbArticle() {
  const { id } = useParams();
  const isNew = id === 'new';
  const articleId = isNew ? null : Number(id);
  const navigate = useNavigate();

  const loaded = useArticleStaff(articleId);
  const topics = useTopics();
  const categoryTree = useCategoriesAll();
  const create = useCreateArticle();
  const patch = usePatchArticle(articleId ?? 0);
  const publish = usePublishArticle(articleId ?? 0);
  const retire = useRetireArticle(articleId ?? 0);

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [type, setType] = useState<SdArticleType>('howto');
  const [audience, setAudience] = useState<SdAudience[]>(['corp']);
  const [topicSlugs, setTopicSlugs] = useState<string[]>([]);
  const [cats, setCats] = useState<SdArticleCategory[]>([]);
  const [pick, setPick] = useState<number | null>(null);
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [message, setMessage] = useState<string | null>(null);

  // Fill the form when the saved article arrives (and again after each save).
  const a = loaded.data;
  useEffect(() => {
    if (!a) return;
    setTitle(a.title);
    setSummary(a.summary ?? '');
    setType(a.article_type);
    setAudience(a.audience);
    setTopicSlugs(a.topic_slugs ?? a.topics);
    setCats(a.categories ?? []);
    setHtml(sdHtml(a.body_html));
    setText(a.body_text ?? '');
    setNote('');
    setEditorKey((k) => k + 1);
  }, [a?.id, a?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploader = useMemo(() => (articleId !== null ? articleImageUploader(articleId) : undefined), [articleId]);

  const input = (): SdArticleInput => ({
    title: title.trim(),
    summary: summary.trim(),
    article_type: type,
    audience,
    topics: topicSlugs,
    category_ids: cats.map((c) => c.id),
    body_html: sdHtmlForStorage(html),
    body_text: text,
    ...(note.trim() ? { change_note: note.trim() } : {}),
  });

  const canSave = title.trim().length > 0 && audience.length > 0;
  const busy = create.isPending || patch.isPending || publish.isPending || retire.isPending;
  const status = a?.status;

  async function saveDraft(): Promise<boolean> {
    setMessage(null);
    try {
      if (isNew) {
        const made = await create.mutateAsync(input());
        navigate(`/it/service-desk/kb/${made.id}`, { replace: true });
        return true;
      }
      await patch.mutateAsync(input());
      setMessage('Saved.');
      return true;
    } catch {
      return false;
    }
  }

  async function doPublish() {
    if (isNew) return;
    if (await saveDraft()) {
      await publish.mutateAsync();
      setMessage('Published. People in the chosen audiences can see it now.');
    }
  }

  const tree = useMemo(() => activeOnly(categoryTree.data ?? []), [categoryTree.data]);
  const addCategory = () => {
    if (pick === null || cats.some((c) => c.id === pick)) return;
    const path = pathLabel(categoryTree.data ?? [], pick);
    if (!path) return;
    setCats((cur) => [...cur, { id: pick, path }]);
    setPick(null);
  };

  const toggleAudience = (x: SdAudience) =>
    setAudience((cur) => (cur.includes(x) ? cur.filter((y) => y !== x) : [...cur, x]));

  return (
    <SdShell
      title={isNew ? 'New article' : (a?.title ?? 'Article')}
      back={{ to: '/it/service-desk/kb', label: 'Back to Knowledge base' }}
    >
      {!isNew && loaded.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading article...</p>
      ) : !isNew && (loaded.error || !a) ? (
        <p className={`text-sm ${SD_MUTED}`}>This article could not be opened.</p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className={`${SD_CARD} flex min-w-0 flex-col gap-4`}>
            <div className="flex flex-wrap items-center gap-2">
              {status && (
                <Pill tone={ARTICLE_STATUS_TONES[status]}>{ARTICLE_STATUS_LABELS[status] ?? status}</Pill>
              )}
              {a && <span className={`text-[12px] ${SD_MUTED}`}>Version {a.version}, updated {formatInstant(a.updated_at)}</span>}
            </div>
            <div>
              <FilterLabel htmlFor="sd-kb-title">Title</FilterLabel>
              <input id="sd-kb-title" value={title} onChange={(e) => setTitle(e.target.value)} className={SD_INPUT} />
            </div>
            <div>
              <FilterLabel htmlFor="sd-kb-summary">Summary (one or two sentences)</FilterLabel>
              <textarea
                id="sd-kb-summary"
                rows={2}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className={SD_INPUT}
              />
            </div>
            <div>
              <FilterLabel as="div">Article</FilterLabel>
              <RichTextEditor
                key={editorKey}
                value={html}
                onChange={(h, t) => {
                  setHtml(h);
                  setText(t);
                }}
                onInsertImage={uploader ?? uploadEditorImage}
                aria-label="Article body"
                className="min-h-[22rem]"
              />
              {isNew && <p className={`mt-1 text-[12px] ${SD_MUTED}`}>Save the draft once to be able to add images.</p>}
            </div>
            {!isNew && (
              <div>
                <FilterLabel htmlFor="sd-kb-note">What changed (saved with the version)</FilterLabel>
                <input id="sd-kb-note" value={note} onChange={(e) => setNote(e.target.value)} className={SD_INPUT} />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={!canSave || busy} onClick={() => void saveDraft()} className={SD_BTN_GHOST}>
                {busy && <Loader2 size={14} className="animate-spin" aria-hidden />}
                Save draft
              </button>
              {!isNew && status !== 'published' && (
                <button type="button" disabled={!canSave || busy} onClick={() => void doPublish()} className={SD_BTN_PRIMARY}>
                  Publish
                </button>
              )}
              {!isNew && status === 'published' && (
                <button type="button" disabled={!canSave || busy} onClick={() => void doPublish()} className={SD_BTN_PRIMARY}>
                  Save and publish changes
                </button>
              )}
              {!isNew && status !== 'retired' && (
                <button
                  type="button"
                  disabled={busy}
                  className={SD_BTN_DANGER}
                  onClick={() => {
                    if (window.confirm('Retire this article? People will stop seeing it in the Help button.')) {
                      retire.mutate(undefined, { onSuccess: () => setMessage('Retired.') });
                    }
                  }}
                >
                  Retire
                </button>
              )}
              {message && <span className={`text-[13px] ${SD_MUTED}`}>{message}</span>}
            </div>
            <SdError error={create.error ?? patch.error ?? publish.error ?? retire.error} what="save the article" />
          </div>

          <aside className="flex flex-col gap-4">
            <div className={SD_CARD}>
              <FilterLabel htmlFor="sd-kb-type">Type</FilterLabel>
              <select id="sd-kb-type" value={type} onChange={(e) => setType(e.target.value as SdArticleType)} className={SD_INPUT}>
                {Object.entries(ARTICLE_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <div className="mt-4">
                <FilterLabel as="div">Who can read it</FilterLabel>
                <div className="flex flex-col gap-1.5 text-[13px] text-amp-ink dark:text-amp-dark-ink">
                  {EDITABLE_AUDIENCES.map((x) => (
                    <label key={x} className="flex items-center gap-2">
                      <input type="checkbox" checked={audience.includes(x)} onChange={() => toggleAudience(x)} />
                      {AUDIENCE_LABELS[x]}
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <FilterLabel as="div">Related categories</FilterLabel>
                <CategoryPicker tree={tree} value={pick} onChange={(id) => setPick(id)} idPrefix="sd-kb-cat" />
                <button type="button" disabled={pick === null} onClick={addCategory} className={`${SD_BTN_GHOST} mt-2`}>
                  Add
                </button>
                {cats.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-1.5 text-[13px]">
                    {cats.map((c) => (
                      <li key={c.id} className="flex items-start justify-between gap-2">
                        <span>{c.path}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${c.path}`}
                          onClick={() => setCats((cur) => cur.filter((x) => x.id !== c.id))}
                          className="text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mt-4">
                <FilterLabel as="div">Topics</FilterLabel>
                <SearchableSelect
                  options={(topics.data ?? []).filter((t) => t.is_active).map((t) => ({ value: t.slug, label: t.name }))}
                  selected={topicSlugs}
                  onChange={setTopicSlugs}
                  placeholder="Add topics"
                  ariaLabel="Topics"
                  className="w-full"
                />
              </div>
            </div>

            {a && a.versions.length > 0 && (
              <div className={SD_CARD}>
                <FilterLabel as="div">Version history</FilterLabel>
                <ul className="flex flex-col gap-2 text-[13px]">
                  {[...a.versions]
                    .sort((x, y) => y.version - x.version)
                    .map((v) => (
                      <li key={v.version}>
                        <span className="font-semibold text-amp-ink dark:text-amp-dark-ink">Version {v.version}</span>
                        <span className={`block text-[12px] ${SD_MUTED}`}>
                          {formatInstant(v.created_at)}
                          {v.author_name ? `, ${v.author_name}` : ''}
                        </span>
                        {v.change_note && <span className="block text-[12px]">{v.change_note}</span>}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      )}
    </SdShell>
  );
}
